import { CatalogImportLimits } from '@raptor/contracts';
import type { Workbook } from 'exceljs';

export type CatalogWorkbookSheet = { name: string; index: number; rows: string[][]; headerRowNumber: number; headers: string[] };
const dangerousCellPrefix = /^[=+\-@]/;

function isCsvFile(file: File): boolean {
  return file.type === 'text/csv' || /\.csv$/i.test(file.name);
}

/** Parses RFC 4180-style CSV text without interpreting cells as formulas. */
export function parseCatalogCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  const pushCell = () => {
    row.push(assertCatalogCellIsSafe(cell));
    cell = '';
  };
  const pushRow = () => {
    pushCell();
    if (row.some(Boolean)) rows.push(row);
    row = [];
  };

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]!;
    if (quoted) {
      if (character === '"') {
        if (text[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += character;
      }
      continue;
    }
    if (character === '"') {
      if (cell) throw new Error('CSV quotes must begin at the start of a cell.');
      quoted = true;
    } else if (character === ',') {
      pushCell();
    } else if (character === '\n') {
      pushRow();
    } else if (character !== '\r') {
      cell += character;
    }
  }
  if (quoted) throw new Error('CSV contains an unterminated quoted cell.');
  if (cell || row.length > 0) pushRow();
  return rows;
}

/** Formula policy: formulas are never evaluated; formula-like cells are rejected. */
export function assertCatalogCellIsSafe(value: unknown): string {
  const text = String(value ?? '').trim();
  if (text && dangerousCellPrefix.test(text)) throw new Error('Formula-like cell values are not accepted in catalog imports.');
  return text;
}

/** Parses a local CSV/XLSX without executing formulas or macros. */
export async function parseCatalogWorkbook(file: File): Promise<CatalogWorkbookSheet[]> {
  if (file.size > CatalogImportLimits.maxBytes) throw new Error('Catalog files must not exceed 25 MiB.');
  if (isCsvFile(file)) {
    const rows = parseCatalogCsv((await file.text()).replace(/^\uFEFF/, ''));
    const headerIndex = rows.findIndex((row) => row.filter(Boolean).length >= 2);
    if (headerIndex >= 0 && rows.length - headerIndex - 1 > CatalogImportLimits.maxRowsPerSheet) throw new Error('A worksheet exceeds the 100,000-row catalog limit.');
    if (rows.some((row) => row.length > CatalogImportLimits.maxColumns)) throw new Error('A worksheet exceeds the 200-column catalog limit.');
    return headerIndex < 0 ? [] : [{ name: 'CSV', index: 0, rows, headerRowNumber: headerIndex + 1, headers: rows[headerIndex]! }];
  }
  let workbook: Workbook;
  try {
    const ExcelJS = await import('exceljs');
    workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
  } catch {
    // ExcelJS/ZIP parser diagnostics are not part of the Catalog UI contract.
    throw new Error('Workbook could not be read.');
  }
  if (workbook.worksheets.length === 0 || workbook.worksheets.length > CatalogImportLimits.maxSheets) throw new Error('The workbook has too many sheets.');
  return workbook.worksheets.map((worksheet, index) => {
    const rows: string[][] = [];
    worksheet.eachRow({ includeEmpty: false }, (row) => {
      const values: string[] = [];
      for (let column = 1; column <= row.cellCount; column += 1) {
        const cell = row.getCell(column);
        if (cell.formula) throw new Error('Workbook formulas are not accepted in catalog imports.');
        values.push(assertCatalogCellIsSafe(cell.text));
      }
      if (values.some(Boolean)) rows.push(values);
    });
    const headerIndex = rows.findIndex((row) => row.filter(Boolean).length >= 2);
    if (headerIndex >= 0 && rows.length - headerIndex - 1 > CatalogImportLimits.maxRowsPerSheet) throw new Error('A worksheet exceeds the 100,000-row catalog limit.');
    if (rows.some((row) => row.length > CatalogImportLimits.maxColumns)) throw new Error('A worksheet exceeds the 200-column catalog limit.');
    return { name: worksheet.name, index, rows, headerRowNumber: headerIndex + 1, headers: headerIndex >= 0 ? rows[headerIndex]! : [] };
  }).filter((sheet) => sheet.headerRowNumber > 0 && sheet.headers.length > 0);
}
